import { DataView } from "@/features/common/components/dataview/DataView";
import { GalleryItem, GalleryItemProperty } from "@/features/common/components/dataview/GalleryItem";
import { MoneyDisplay } from "@/ds/custom/MoneyDisplay";
import { NumericInput } from "@/ds/custom/NumericInput";
import { SaleVolumeGraph } from "@/features/common/components/SaleVolumeGraph";
import { SmallProductDisplay } from "@/features/common/components/SmallProductDisplay";
import { useCollectionPerformanceDeltas, type CollectionPerformanceDeltaResponseModel } from "@/features/collections/api/useCollectionPerformanceDeltas";
import { SortAscIcon, TimerIcon } from "lucide-react";
import { useCallback, useMemo, useRef, useState, type FC } from "react";
import { SearchBar } from "@/features/common/components/SearchBar";
import { HeaderBarSelector } from "@/features/common/components/HeaderBarSelector";
import { PerformanceDeltaSortOptions } from "@/features/performanceDeltas/PerformanceDeltaSort";
import { PerformanceDeltaStepOptions } from "@/features/performanceDeltas/PerformanceDeltaStep";
import { useUserPreferencesState, type UserPreferencesState } from "@/lib/state/UserPreferencesState";
import { SubscriptionGate } from "@/features/common/components/SubscriptionGate";
import { TableCell } from "@/ds/shadcn/components/ui/table";
import { ListItem } from "@/features/common/components/dataview/ListItem";
import { SplitScreen, SplitScreenLeftPanel, SplitScreenRightPanel } from "@/features/common/components/SplitScreen";
import { ProductView } from "@/features/products/components/ProductView";
import { SearchResults } from "@/features/common/components/SearchResults";
import { HeaderBarTools } from "@/features/common/components/HeaderBarTools";
import { useParams } from "react-router";

export const TabCollectionPerformanceDeltas: FC = () => {
	const params = useParams();
	const collectionId = params.collectionId as string;

	const { state, dispatchSetPerformanceDeltaSort, dispatchSetPerformanceDeltaStep, dispatchSetPerformanceDeltaMinimum } = useUserPreferencesState();

	return (
		<>
			<HeaderBarTools>
				<HeaderBarSelector
					value={state.performanceDeltaSort}
					onChange={dispatchSetPerformanceDeltaSort}
					placeholder="Sort"
					icon={SortAscIcon}
					options={PerformanceDeltaSortOptions} />

				<HeaderBarSelector
					value={state.performanceDeltaStep}
					onChange={dispatchSetPerformanceDeltaStep}
					placeholder="Time Range"
					icon={TimerIcon}
					options={PerformanceDeltaStepOptions} />

				<NumericInput
					className="@md:max-w-48"
					value={state.performanceDeltaMinimum}
					onChange={dispatchSetPerformanceDeltaMinimum}
					prefix="Min"
					maxDecimalPlaces={0}
					minValue={0}
					maxValue={10000} />
			</HeaderBarTools>

			<SubscriptionGate featureName="Collection Trends">
				<TabCollectionPerformanceDeltasInternal collectionId={collectionId} state={state} />
			</SubscriptionGate>
		</>
	);
};

interface TabCollectionPerformanceDeltasInternalProps {
	collectionId: string;
	state: UserPreferencesState;
}

const TabCollectionPerformanceDeltasInternal: FC<TabCollectionPerformanceDeltasInternalProps> = props => {
	const {
		collectionId,
		state
	} = props;

	const [searchQuery, setSearchQuery] = useState("");
	const performanceDeltas = useCollectionPerformanceDeltas(collectionId, searchQuery, state.performanceDeltaStep, state.performanceDeltaMinimum, state.performanceDeltaSort);
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
	const additionalProps = useMemo(() => ({
		onSelectProduct: setSelectedProductId
	}), [setSelectedProductId]);

	return (
		<>
			<SearchBar value={searchQuery} onChange={setSearchQuery} />
			<SplitScreen fixed visible={!!selectedProductId} onVisibleChange={visible => !visible && setSelectedProductId(null)}>
				<SplitScreenLeftPanel ref={scrollRef}>
					<SearchResults searchQuery={searchQuery} forQuery={performanceDeltas}>
						<DataView
							forQuery={performanceDeltas}
							getKey={x => x.variantId}
							listViewHeaderLabels={["Product", "Sale Volumes", "Gain", "Current Price", "Previous Price"]}
							listViewHeaderClassNames={["min-w-0", "w-46 @2xl:w-64", "w-46 @2xl:w-64", "w-30 @2xl:w-48", "w-30 @2xl:w-48"]}
							listComponent={TabCollectionPerformanceDeltasDataViewListItem}
							galleryComponent={TabCollectionPerformanceDeltasDataViewGalleryItem}
							additionalProps={additionalProps}
							scrollRef={scrollRef}
							galleryMinWidth={208} />
					</SearchResults>
				</SplitScreenLeftPanel>
				<SplitScreenRightPanel>
					{
						selectedProductId &&
						<ProductView className="min-h-full" productId={selectedProductId} />
					}
				</SplitScreenRightPanel>
			</SplitScreen>
		</>
	);
};

interface TabCollectionPerformanceDeltasDataViewProps extends CollectionPerformanceDeltaResponseModel {
	onSelectProduct: (productId: string) => void;
};

const TabCollectionPerformanceDeltasDataViewListItem: FC<TabCollectionPerformanceDeltasDataViewProps> = props => {
	const {
		productId,
		saleVolumes,
		delta,
		previousPrice,
		currentPrice,
		onSelectProduct
	} = props;

	const onClick = useCallback(() => onSelectProduct(productId), [onSelectProduct, productId]);

	return (
		<ListItem onClick={onClick}>
			<TableCell>
				<SmallProductDisplay {...props} />
			</TableCell>
			<TableCell><SaleVolumeGraph data={saleVolumes} /></TableCell>
			<TableCell><MoneyDisplay money={delta} mode="DeltaPercentage" originalValue={previousPrice.value} /></TableCell>
			<TableCell><MoneyDisplay money={currentPrice} mode="Standard" /></TableCell>
			<TableCell><MoneyDisplay money={previousPrice} mode="Standard" /></TableCell>
		</ListItem>
	);
};

const TabCollectionPerformanceDeltasDataViewGalleryItem: FC<TabCollectionPerformanceDeltasDataViewProps> = props => {
	const {
		productId,
		productName,
		fcId,
		imageUrl,
		delta,
		currentPrice,
		previousPrice,
		onSelectProduct
	} = props;

	const onClick = useCallback(() => onSelectProduct(productId), [onSelectProduct, productId]);

	return (
		<GalleryItem title={productName} description={fcId} imageUrl={imageUrl} onClick={onClick}>
			<GalleryItemProperty label="Price">
				<MoneyDisplay money={currentPrice} mode="Standard" />
			</GalleryItemProperty>
			<GalleryItemProperty label="Gain">
				<MoneyDisplay money={delta} mode="DeltaPercentage" originalValue={previousPrice.value} />
			</GalleryItemProperty>
		</GalleryItem>
	);
};
